/**
 * Catalog mirroring the backend enums used by
 * GET/PUT /v1/clients/{clientUuid}/docusign/templates/{templateType}
 *
 * - DOCUSIGN_TEMPLATE_TYPES  -> DocusignTemplateType
 * - DOCUSIGN_ROLE_NAMES      -> the only role names DocusignService maps to a recipient
 * - DOCUSIGN_INTERNAL_FIELDS -> DocusignInternalField (tabLabel is only a suggestion,
 *   the backend sends the configured templateName as the tabLabel of the text tab)
 */

export const DOCUSIGN_TEMPLATE_TYPES = [
    { value: 'PROPOSAL_LETTER_INVOICE_FACTORING', label: 'Proposal Letter Invoice Factoring' }
]

export const DOCUSIGN_ROLE_NAMES = [
    { value: 'Seller', label: 'Seller', recipient: "First application owner's name and email" },
    { value: 'Purchaser', label: 'Purchaser', recipient: "Client's name and email" }
]

export const DOCUSIGN_FIELD_GROUPS = {
    TERMS: 'Terms',
    FACTORING_FEES: 'Factoring Fees',
    BUSINESS: 'Business',
    OWNERS: 'Owners & Contact'
}

export const DOCUSIGN_INTERNAL_FIELDS = [
    // Terms
    { value: 'ADVANCE_RATE', tabLabel: 'AdvanceRate', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · advance rate', format: '12.34%' },
    { value: 'PURCHASE_RATE', tabLabel: 'PurchaseRate', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · purchase rate', format: '12.34%' },
    { value: 'FEE_PER_INVOICE', tabLabel: 'FeePerInvoice', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · fee per invoice', format: '$12.34' },
    { value: 'MINIMUM_PURCHASE_FEE', tabLabel: 'MinimumPurchaseFee', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · minimum purchase fee', format: '$12.34' },
    { value: 'LATE_CHARGE', tabLabel: 'LateCharge', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · late charge', format: '12.34%' },
    { value: 'RECOURSE_PERIOD', tabLabel: 'RecoursePeriod', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · recourse period', format: 'integer' },
    { value: 'TERM', tabLabel: 'Term', group: DOCUSIGN_FIELD_GROUPS.TERMS, source: 'Terms · term auto renew', format: 'integer' },
    // Factoring fees (tolerate missing data, may render as an empty string)
    { value: 'FACTORING_FEES_PERCENTAGE_FIRST_PERIOD', tabLabel: 'FactoringFeesPercentageFirstPeriod', group: DOCUSIGN_FIELD_GROUPS.FACTORING_FEES, source: 'Factoring fees · first period', format: '12.34% or empty' },
    { value: 'FACTORING_FEES_DAYS_FIRST_PERIOD', tabLabel: 'FactoringFeesDaysFirstPeriod', group: DOCUSIGN_FIELD_GROUPS.FACTORING_FEES, source: 'Factoring fees · first period', format: 'integer or empty' },
    { value: 'FACTORING_FEES_PERCENTAGE_SUBSEQUENT_PERIOD', tabLabel: 'FactoringFeesPercentageSubsequentPeriod', group: DOCUSIGN_FIELD_GROUPS.FACTORING_FEES, source: 'Factoring fees · subsequent period', format: '12.34% or empty' },
    { value: 'FACTORING_FEES_DAYS_SUBSEQUENT_PERIOD', tabLabel: 'FactoringFeesDaysSubsequentPeriod', group: DOCUSIGN_FIELD_GROUPS.FACTORING_FEES, source: 'Factoring fees · subsequent period', format: 'integer or empty' },
    // Business
    { value: 'BUSINESS_NAME', tabLabel: 'BusinessName', group: DOCUSIGN_FIELD_GROUPS.BUSINESS, source: 'Business name', format: 'text' },
    { value: 'BUSINESS_ADDRESS', tabLabel: 'BusinessAddress', group: DOCUSIGN_FIELD_GROUPS.BUSINESS, source: 'Business address line 1', format: 'text' },
    { value: 'BUSINESS_CITY_STATE_ZIP', tabLabel: 'BusinessCityState&Zip', group: DOCUSIGN_FIELD_GROUPS.BUSINESS, source: 'City, state zip', format: 'text' },
    { value: 'FULL_BUSINESS_ADDRESS', tabLabel: 'FullBusinessAddress', group: DOCUSIGN_FIELD_GROUPS.BUSINESS, source: 'Address line 1, city, state zip', format: 'text' },
    { value: 'MC_NUMBER', tabLabel: 'MCNumber', group: DOCUSIGN_FIELD_GROUPS.BUSINESS, source: 'Business MC number', format: 'text' },
    { value: 'DOT_NUMBER', tabLabel: 'DOTNumber', group: DOCUSIGN_FIELD_GROUPS.BUSINESS, source: 'Business DOT number', format: 'text' },
    // Owners & contact
    { value: 'OWNER_NAMES', tabLabel: 'OwnerNames', group: DOCUSIGN_FIELD_GROUPS.OWNERS, source: 'First owner (first + last name)', format: 'text' },
    { value: 'OWNER_TITLE', tabLabel: 'OwnerTitle', group: DOCUSIGN_FIELD_GROUPS.OWNERS, source: 'First owner title', format: 'text' },
    { value: 'GUARANTORS', tabLabel: 'Guarantors', group: DOCUSIGN_FIELD_GROUPS.OWNERS, source: 'Every owner, comma separated', format: 'text' },
    { value: 'CONTACT_NAME', tabLabel: 'ContactName', group: DOCUSIGN_FIELD_GROUPS.OWNERS, source: 'First owner', format: 'text' },
    { value: 'CONTACT_EMAIL', tabLabel: 'ContactEmail', group: DOCUSIGN_FIELD_GROUPS.OWNERS, source: 'First owner email', format: 'text' },
    { value: 'CONTACT_TELEPHONE', tabLabel: 'ContactTelephone', group: DOCUSIGN_FIELD_GROUPS.OWNERS, source: 'First owner phone', format: 'text' }
]

export const DOCUSIGN_FIELD_GROUP_ORDER = [
    DOCUSIGN_FIELD_GROUPS.TERMS,
    DOCUSIGN_FIELD_GROUPS.FACTORING_FEES,
    DOCUSIGN_FIELD_GROUPS.BUSINESS,
    DOCUSIGN_FIELD_GROUPS.OWNERS
]

export const getInternalField = (internalName) =>
    DOCUSIGN_INTERNAL_FIELDS.find((field) => field.value === internalName)

export const getTemplateTypeLabel = (templateType) =>
    DOCUSIGN_TEMPLATE_TYPES.find((type) => type.value === templateType)?.label || templateType
